import {
  PositionFill18Icon,
  PositionOutline18Icon,
} from "@vygruppen/spor-icon-react";
import { isFuture } from "date-fns";
import ElementWrapper from "features/VehicleSidebar/VehicleList/common/ElementWrapper";
import { FC } from "react";
import { generatePath, useSearchParams } from "react-router-dom";
import { CallButton } from "shared/components/buttons/CallButton";
import { TimeInterval } from "shared/components/dataDisplay/TimeInterval";
import {
  Badge,
  HStack,
  SporTooltip,
  Stack,
  Text,
  TooltipContent,
  TooltipTrigger,
  VStack,
} from "shared/components/Spor";
import { ReserveAlternativeTransport } from "shared/types/alternativeTransport";
import {
  getPrintableFromVehicleType,
  isBackupFromPlan,
} from "shared/utils/alternativeTransport";
import { BACKUPBUS } from "shared/utils/routes";
import { useTheme } from "styled-components";

type Props = {
  reserveAlternativeTransport: ReserveAlternativeTransport;
  elementSelected: boolean;
  isLastElement?: boolean;
};

/**
 * Beredskapsbuss-specific list element.
 *
 * NB! Temporary duplicate of ReserveOrBackupAlternativeTransportElement so that
 * beredskapsbuss work does not bleed into the reservebuss element.
 */
export const BackupAlternativeTransportElement: FC<Props> = ({
  reserveAlternativeTransport,
  elementSelected,
  isLastElement = false,
}) => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();

  const {
    shiftStartTime,
    shiftEndTime,
    driverPhoneNumber,
    isAvailable,
    baseStopName,
    type,
    hasTracking,
    workShiftId,
  } = reserveAlternativeTransport;

  const hasPhoneNumber = !!driverPhoneNumber && driverPhoneNumber.length > 0;

  const isFutureShift = shiftEndTime && isFuture(shiftEndTime);

  const isPast = !isFutureShift;

  const textColorMain = isPast
    ? theme.colorTextTertiary
    : theme.colorTextSecondary;

  const providerTitle = isBackupFromPlan(reserveAlternativeTransport)
    ? "Hastus"
    : "Drops";
  const vehicleTitle = `Beredskap ${workShiftId}`;

  return (
    <ElementWrapper
      selected={elementSelected}
      backgroundColor={theme.colorBackgroundSecondary}
      color={textColorMain}
      isHover
      to={`${generatePath(BACKUPBUS, {
        vehicleUuid: reserveAlternativeTransport.id,
      })}?${searchParams.toString()}`}
      linkState={{ vehicleTitle }}
      hideDashedLine={isLastElement}
    >
      <Stack
        flex={1}
        _focusVisible={{
          borderRadius: "6px",
        }}
      >
        <HStack
          fontSize="xs"
          gap="0.25rem"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <VStack alignItems="flex-start" gap="0">
            <HStack>
              <Text fontWeight="bold" color={textColorMain} variant="sm">
                {vehicleTitle}
              </Text>
              <Text color={textColorMain} variant="sm">
                {providerTitle}
              </Text>
            </HStack>

            <Text variant="sm">{`${baseStopName} (${getPrintableFromVehicleType(type, "sentence") ?? ""})`}</Text>
            <TimeInterval
              startTime={shiftStartTime}
              endTime={shiftEndTime}
              disabled={isPast}
              variant="sm"
            />
          </VStack>

          <VStack
            alignItems="flex-end"
            justifyContent="space-between"
            alignSelf="stretch"
          >
            <HStack>
              {!isAvailable && <Badge colorPalette="blue">I bruk</Badge>}
              <SporTooltip>
                {hasTracking ? (
                  <>
                    <TooltipTrigger>
                      <PositionFill18Icon color={theme.colorSuccess} />
                    </TooltipTrigger>
                    <TooltipContent>
                      <Text variant="sm">Denne bussen har aktiv sporing</Text>
                    </TooltipContent>
                  </>
                ) : (
                  <>
                    <TooltipTrigger>
                      <PositionOutline18Icon color={theme.colorSuccess} />
                    </TooltipTrigger>
                    <TooltipContent>
                      <Text variant="sm">
                        Denne bussen har ikke aktiv sporing
                      </Text>
                    </TooltipContent>
                  </>
                )}
              </SporTooltip>
            </HStack>

            {hasPhoneNumber && (
              <CallButton phoneNumber={driverPhoneNumber} size="xs" />
            )}
          </VStack>
        </HStack>
      </Stack>
    </ElementWrapper>
  );
};
